import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Check, Loader2 } from "lucide-react";

import { API } from "../lib/helpers";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import TripwireOffer from "./TripwireOffer";

/**
 * AnalysisSaveCard — the Phase 1B lead-magnet CTA.
 *
 * Presented AFTER the anonymous visitor has already seen the full free
 * analysis (per approved plan: value first, ask second). Renders inside
 * AnalysisModal, below the results.
 *
 * Correctly-instrumented funnel events:
 *   - `report_email_prompt_viewed` fires when the CTA is genuinely visible
 *     on screen (IntersectionObserver, ≥50% intersection). It does NOT
 *     fire on mere component mount — otherwise the metric loses meaning
 *     the moment the CTA is hidden below the fold.
 *   - `lead_captured` fires ONLY after the backend confirms persistence.
 *     Both come from the server-side capture endpoint, not the client, so
 *     they cannot be double-fired by React StrictMode or retries.
 *
 * Marketing consent is a SEPARATE checkbox. Submitting an email does not
 * imply consent to marketing emails — it only consents to the
 * transactional report delivery the button explicitly promises.
 *
 * Props:
 *   analysisId        — the anonymous session ID from useAnalysisSession
 *   propertyAddress   — displayed inline for context ("save 123 Main St")
 *   onCaptured        — optional callback invoked after successful capture
 *   trackFunnelEvent  — fire-and-forget helper from useAnalysisSession
 */
export default function AnalysisSaveCard({
  analysisId,
  propertyAddress = "this property",
  onCaptured,
  trackFunnelEvent,
}) {
  const cardRef = useRef(null);
  const viewedFiredRef = useRef(false);

  const [email, setEmail] = useState("");
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [deliveryFailed, setDeliveryFailed] = useState(false);

  // Fire `report_email_prompt_viewed` exactly once, when the CTA is
  // actually visible. This avoids two bad patterns:
  //   1. Firing on mount (would count users who never scrolled to the CTA)
  //   2. Firing on every scroll (would inflate the count)
  useEffect(() => {
    if (viewedFiredRef.current) return;
    const el = cardRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            viewedFiredRef.current = true;
            trackFunnelEvent?.("report_email_prompt_viewed", {
              analysis_id: analysisId,
            });
            observer.disconnect();
            break;
          }
        }
      },
      { threshold: [0.5] },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [analysisId, trackFunnelEvent]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!analysisId) {
      // Should never happen — parent must not render without a session.
      toast.error("Analysis not ready — please refresh and try again");
      return;
    }
    if (!email) return;
    setSubmitting(true);
    setDeliveryFailed(false);
    try {
      const { data } = await axios.post(`${API}/leads/capture`, {
        analysis_id: analysisId,
        email,
        marketing_consent: marketingConsent,
        source: "results_page",
      });
      setDone(true);
      if (data?.email_delivered === false) {
        setDeliveryFailed(true);
      }
      onCaptured?.(data);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        toast.error("This analysis has already been claimed by another email");
      } else if (status === 404) {
        toast.error("Analysis session expired — please re-run");
      } else {
        toast.error("Could not save — please try again in a moment");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Post-success state — same footprint so no layout shift on transition.
  if (done) {
    return (
      <>
        <div
          ref={cardRef}
          data-testid="analysis-save-card-success"
          className="mt-6 border border-emerald-500/30 bg-emerald-500/5 rounded-xl p-5"
        >
          <div className="flex items-start gap-3">
            <Check className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-semibold text-foreground mb-1">You&apos;re all set</div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {deliveryFailed
                  ? "We saved this analysis, but the email is taking longer than expected — we'll retry delivery shortly."
                  : `Your analysis for ${propertyAddress} is on its way. Check your inbox in a minute.`}
              </p>
            </div>
          </div>
        </div>
        {/* Tripwire — Phase 2A. Surface the $7 Deep-Dive at the moment
            the visitor has just proven high intent (they gave us their
            email). The offer component fires `tripwire_viewed` on mount
            and hands off checkout via `POST /api/tripwire/checkout`. */}
        <TripwireOffer analysisId={analysisId} trackFunnelEvent={trackFunnelEvent} />
      </>
    );
  }

  return (
    <div
      ref={cardRef}
      data-testid="analysis-save-card"
      className="mt-6 border border-border bg-background/40 rounded-xl p-5"
    >
      <div className="flex items-start gap-3 mb-4">
        <Mail className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
        <div>
          <div className="font-semibold text-foreground mb-1">
            Save this deal + get your PDF report
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed">
            We&apos;ll email you a shareable PDF of this analysis and a link that
            re-opens the exact same numbers whenever you want.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        <Input
          type="email"
          required
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={submitting}
          data-testid="analysis-save-card-email"
          autoComplete="email"
          className="bg-background"
        />

        {/* Marketing consent is EXPLICITLY separate from the transactional
            report delivery. Unchecked = they still get the report they asked
            for. Checked = they opt in to educational emails on top of it. */}
        <label
          className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer select-none"
          data-testid="analysis-save-card-consent-row"
        >
          <input
            type="checkbox"
            checked={marketingConsent}
            onChange={(e) => setMarketingConsent(e.target.checked)}
            disabled={submitting}
            data-testid="analysis-save-card-consent"
            className="mt-0.5 h-3.5 w-3.5 accent-emerald-500"
          />
          <span className="leading-snug">
            Also send me occasional real-estate investing tips and deal breakdowns.
            <span className="block text-muted-foreground/70 mt-0.5">
              Optional — unrelated to your report delivery.
            </span>
          </span>
        </label>

        <Button
          type="submit"
          disabled={submitting || !email}
          className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
          data-testid="analysis-save-card-submit"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…
            </>
          ) : (
            "Save this deal + email me the PDF"
          )}
        </Button>
      </form>
    </div>
  );
}
